// The public release log at /whats-new, syndicated as /whats-new.xml. One entry per WEEK, not per release.
// PURE DATA only — vite.config.ts imports this outside the app's module graph, where React and `@/` don't resolve.

/** The three kinds of change a release note can carry. Drives the tag chip's colour + label. */
export enum ChangeTag {
  New = 'new',
  Improved = 'improved',
  Fixed = 'fixed'
}

/**
 * English tag labels for the RSS feed only — the feed is generated by `vite.config.ts`, outside the
 * app's module graph, so it has no i18next. Kept beside the enum so adding a tag can't skip the feed.
 */
export const CHANGE_TAG_FEED_LABEL: Record<ChangeTag, string> = {
  [ChangeTag.New]: 'New',
  [ChangeTag.Improved]: 'Improved',
  [ChangeTag.Fixed]: 'Fixed'
}

export interface Change {
  tag: ChangeTag
  /** Plain sentence. `**bold**` marks the phrase the eye should land on. */
  text: string
}

export interface Release {
  /** ISO date of the week (Monday). Doubles as the entry's anchor id — `/whats-new#2026-07-27`. */
  shipDate: string
  /**
   * The real CalVer version these notes correspond to: the last release cut that week.
   * Verifiable against `git tag`. Never invent one — the pill is a factual claim.
   */
  version: string
  headline: string
  /** Optional chip beside the headline, marking the week's character at a glance. */
  featureTag?: ChangeTag
  /** The week's lead story. Omit for a quiet week — the entry then renders in its lighter shape. */
  prose?: string
  changes: Change[]
}

// Newest first. The first entry gets the "New this week" badge automatically.
export const releases: Release[] = [
  {
    shipDate: '2026-10-26',
    version: '2026.1026.0',
    headline: 'Your plans are easier to find on a phone',
    featureTag: ChangeTag.Improved,
    prose:
      '**On a phone, the way into all your plans now says "All plans"** instead of an unlabelled clock icon, and sits on its own at the right of the header — easier to spot and easier to tap, and screen readers announce it properly.',
    changes: [
      {
        tag: ChangeTag.Improved,
        text: "**Each Pro Level-up now reads as a single line** on a meal — what's done, how long it takes and what it adds to the plate."
      },
      {
        tag: ChangeTag.Fixed,
        text: "**Hovering a dinner in a past plan highlights the whole row**, so it's clear which meal you're about to open."
      }
    ]
  },
  {
    shipDate: '2026-10-19',
    version: '2026.1020.0',
    headline: 'Dinners that match the plate you built',
    featureTag: ChangeTag.New,
    prose:
      "**Skip the protein or the starch and you still get complete dinners** — each with its own name, cooking steps and shopping list, built around what's left. Portions follow the plate too: more dots on protein, a bigger serving of protein.",
    changes: [
      {
        tag: ChangeTag.New,
        text: '**Recipes and shopping lists are sized for your household** — plan for six and the amounts grow to match, rounded to what you can actually buy.'
      },
      {
        tag: ChangeTag.New,
        text: '**You get the mix of effort you asked for** — three quick and two involved means exactly that, labelled, quickest first.'
      },
      {
        tag: ChangeTag.New,
        text: '**Plan this again** copies a past week into a new draft — same dinners, same spacing, yours to adjust.'
      },
      {
        tag: ChangeTag.Improved,
        text: "**Meal names and descriptions are shorter and plainer** — what's on the plate and how it's cooked."
      },
      {
        tag: ChangeTag.Improved,
        text: "**A Level-up's extra ingredients now go on the shopping list**, with amounts."
      },
      {
        tag: ChangeTag.Improved,
        text: '**Better at catching what you avoid** — names buried in longer phrases like "golden beet greens", plus squid, octopus, cuttlefish and sea urchin as shellfish.'
      }
    ]
  },
  {
    shipDate: '2026-10-12',
    version: '2026.1012.1',
    headline: "Home opens on tonight's dinner",
    featureTag: ChangeTag.New,
    prose:
      "**Your home page now leads with tonight's dinner — a big photo and a Start cooking button, with the rest of your week below it.** Between plans it shows how last week went and invites you to plan the next one. New accounts get a three-step welcome.",
    changes: [
      {
        tag: ChangeTag.New,
        text: '**Set your cooking effort whichever way suits you** — a usual level with only the days that differ, or just how many quick, moderate and involved dinners you want a week.'
      },
      {
        tag: ChangeTag.New,
        text: '**A grocery budget: Lean, Normal or Generous.** It leans on the ingredients Parsley picks, and you can set a different one for any week.'
      },
      {
        tag: ChangeTag.Improved,
        text: '**A new plan no longer repeats your last one** — the same dish, or the same protein on the same night.'
      },
      {
        tag: ChangeTag.Improved,
        text: '**Asking for more of a protein now works.** Set fish to often and you get at least two fish dinners a week.'
      },
      {
        tag: ChangeTag.Improved,
        text: 'Every eating preference page has a Cancel button, and a **How this works** guide explains what each setting does.'
      }
    ]
  },
  {
    shipDate: '2026-10-05',
    version: '2026.1005.1',
    headline: 'Every ingredient in a meal is one you can eat',
    featureTag: ChangeTag.Improved,
    prose:
      "**Parsley now checks every part of a meal against your exclusions — the marinades and cooking steps as well as the ingredient list.** Exclude dairy and a chicken breast can't be basted in butter. And excluding a food catches its whole family: mushrooms rules out portobello and truffle oil too.",
    changes: [
      {
        tag: ChangeTag.New,
        text: '**Protein balance is a new setting under Eating preferences** — say how often red meat, fish, poultry and plant-based dishes should turn up, from never to often. Your plans now follow it: cap red meat at twice a week and that is what you get.'
      },
      {
        tag: ChangeTag.Improved,
        text: '**A week of dinners now looks like a normal week** — a couple of chicken nights, one or two beef, one or two pork, a fish night. Everyday proteins come round most often, and ones most homes cook now and then, like lamb and bison, turn up less.'
      },
      {
        tag: ChangeTag.Improved,
        text: '**Editing a food profile takes one less tap** — its ingredient rules are on the profile page now, rather than behind a second screen. Creating one is two steps: name it, add any rules.'
      },
      {
        tag: ChangeTag.Fixed,
        text: "A meal photo that fails to load shows that meal's placeholder art instead of a broken-image icon, everywhere meals appear."
      },
      {
        tag: ChangeTag.Fixed,
        text: 'Login tells you what went wrong in every browser — an empty or mistyped email, or a missing password, now shows a message under the field. Firefox used to show nothing at all.'
      },
      {
        tag: ChangeTag.New,
        text: 'A one-time bubble beside the feedback button in the header points out where to share an idea or report a bug.'
      }
    ]
  },
  {
    shipDate: '2026-09-28',
    version: '2026.0928.0',
    headline: 'Find a plan by what you cooked',
    featureTag: ChangeTag.New,
    prose:
      '**Search on All plans now looks inside your plans — the dinners, their ingredients, your review and your notes — and forgives a typo.** Searching salmon finds every plan with a salmon dinner, and chickn still finds chicken. Results come back best match first, and you can re-sort them by newest, rating or most cooked.',
    changes: [
      {
        tag: ChangeTag.New,
        text: '**Peek at a meal without leaving your plan.** Tap a dinner on All plans, or a meal while arranging a draft, and a panel slides in with the photo, cook time, servings, ingredients and steps — your list stays where it was.'
      },
      {
        tag: ChangeTag.New,
        text: '**Rename a plan by tapping its name** — look for the pencil. Food profiles rename the same way.'
      },
      {
        tag: ChangeTag.Improved,
        text: "**Deleting a plan or discarding a draft happens in one tap, and you can undo it** — from the message or the undo button at the top of the screen. Deleting a food profile asks first, since that one can't be undone."
      },
      {
        tag: ChangeTag.Improved,
        text: "**Your plan no longer lists the days you missed.** A dinner you didn't get to stays in your lineup, ready whenever you are, without a past date hanging over it."
      },
      {
        tag: ChangeTag.Fixed,
        text: 'On a phone, the back arrow at the top now goes up a level — from a meal to Recipes, from All plans to Meal Plans — instead of replaying wherever you happened to have been.'
      }
    ]
  },
  {
    shipDate: '2026-09-14',
    version: '2026.0915.1',
    headline: 'A thumbs up or down on ten cuisines',
    featureTag: ChangeTag.Improved,
    prose:
      '**Cuisine preferences are now a thumbs-up or thumbs-down on ten cuisines, instead of a five-point rating on twenty-nine.** A thumbs-up asks Parsley to work those flavours into your dinners, a thumbs-down keeps them out, and leaving one alone means no opinion. As you tap, the panel alongside says what it adds up to — Italian and Thai in the mix, anything but Indian — and the Eating style page echoes the same words.',
    changes: [
      {
        tag: ChangeTag.Improved,
        text: "**If Parsley can't match one of your food restrictions to an ingredient it knows, it now stops** rather than building a plan without it — so an allergy can never be quietly switched off."
      },
      {
        tag: ChangeTag.Fixed,
        text: '**Animal fats and broths are labelled as meat or poultry** — lard, tallow, bacon fat, duck fat, schmaltz, chicken and beef broth, bone broth, gelatin — so ruling out a food rules out everything made from it.'
      },
      {
        tag: ChangeTag.New,
        text: "**A small amber dot tells you when there's something new to read here.** It appears by What's new in the footer, the account menu and on your avatar, clears when you open the page, and fades on its own after a week."
      },
      {
        tag: ChangeTag.Improved,
        text: 'The plate-shape, cuisine, effort and nutrition screens now have a close button, and Escape closes them — like every other full-screen screen in Parsley.'
      }
    ]
  },
  {
    shipDate: '2026-08-31',
    version: '2026.0831.1',
    headline: "A plan doesn't have to be a schedule",
    featureTag: ChangeTag.New,
    prose:
      "**Reviewing a new plan, you can now choose Flexible — no dates at all, just an order you cook through whenever you get to it.** Nothing is ever late, and no screen shows you a day you're behind on. Choose Scheduled and you get the run of days the plan actually covers, including the ones you're not cooking on: drag a dinner onto a day to move it, or nudge it with the arrows, and dropping it on a day that's taken swaps the two.",
    changes: [
      {
        tag: ChangeTag.New,
        text: '**Cuisine preferences is a grid of food now.** Twenty-nine cuisines, up from eight, each with a photo and a five-stop dial running from skip it to a favourite — so you can say how much you want something, not just yes or no.'
      },
      {
        tag: ChangeTag.Fixed,
        text: '**The start date you pick now sticks.** Parsley used to show your choice and then quietly go back to counting from whenever the plan was generated, shifting every dinner with it.'
      },
      {
        tag: ChangeTag.New,
        text: '**You can reset a forgotten password from inside Parsley** — anywhere it asks for your current password, there is now a link to send yourself a reset. Before, you had to sign out first.'
      },
      {
        tag: ChangeTag.Improved,
        text: "Deleting your account now sends a confirmation email naming the plan that was cancelled, and Parsley's terms say what happens to money you've already paid."
      }
    ]
  },
  {
    shipDate: '2026-08-24',
    version: '2026.0824.0',
    headline: 'Parsley now emails you about your subscription',
    featureTag: ChangeTag.New,
    prose:
      "**Until now it never did — a failed payment, an ending trial or a cancellation all passed in silence.** You'll get a heads-up before a trial or beta period ends, a note when it does, an explanation if a card payment fails, a confirmation once it goes through, and a receipt for every renewal. Dates are worked out in your own timezone, so a trial ending Friday means the Friday on your calendar.",
    changes: [
      {
        tag: ChangeTag.New,
        text: "**Deleting your account now says what it costs before it happens** — your plan, price and renewal date, a link to cancelling instead if that's all you wanted, and a password check so an unattended session can't do it."
      },
      {
        tag: ChangeTag.Improved,
        text: '**Profile settings has had a full refresh.** Your name, email and password open in place with bigger fields and a show/hide toggle — and changing your email or adding a password shows up straight away instead of after a reload.'
      },
      {
        tag: ChangeTag.Fixed,
        text: "Signing back in no longer re-announces a plan you've already seen — the plan-ready toast appears when the plan actually finishes, not every time you return to it."
      },
      { tag: ChangeTag.Fixed, text: 'Tapping something on a phone no longer leaves it stuck looking hovered until you tap somewhere else.' }
    ]
  },
  {
    shipDate: '2026-08-17',
    version: '2026.0817.2',
    headline: 'Tell Parsley about the week ahead',
    featureTag: ChangeTag.New,
    prose:
      "**Starting a plan is now a guided walkthrough instead of a small dialog.** It opens by telling you what generating right now would give you, then asks a few optional questions about the week — how busy you are, whether you're hosting, how adventurous you want the recipes — and ends on a recap you can still edit. Skip the lot and generate whenever you like: every question is optional, and your answers apply to that plan only.",
    changes: [
      {
        tag: ChangeTag.Improved,
        text: '**Setting your plate starts from a familiar one** — Veggie-forward, Protein-first, The cozy bowl, Skip the starch — which you nudge up or down with a tap, described in plain words instead of percentages.'
      },
      {
        tag: ChangeTag.New,
        text: '**Cook mode moves with a swipe.** Nudge the step card left for the next step, right to go back — no aiming at a button while your hands are busy.'
      },
      {
        tag: ChangeTag.Fixed,
        text: '**A page that fails to load no longer blanks the app.** You get a page that says so, with a Try again button and a way back to the kitchen.'
      },
      {
        tag: ChangeTag.Fixed,
        text: '**Go to plan** now opens the plan you just accepted, instead of dropping you back on whichever one you were looking at before.'
      }
    ]
  },
  {
    shipDate: '2026-08-10',
    version: '2026.0810.1',
    headline: 'Subscriptions are here',
    featureTag: ChangeTag.New,
    prose:
      "**Every new account starts on a free trial — no card, nothing to fill in**, and invited beta members get six months. When you're ready there are two plans, **Pinch** at $6.99 a month and **Handful** at $9.99, differing only in how much cooking Parsley plans for you — never in what it can do. Your new Subscription page in Settings shows what you're on and when it renews.",
    changes: [
      {
        tag: ChangeTag.New,
        text: '**Tell Parsley when its summary of how you eat is wrong.** A thumbs up or down now sits under the description on your Eating Style page, with an optional note about what it got off.'
      },
      {
        tag: ChangeTag.Improved,
        text: 'If a payment ever fails you keep full access for 14 days, and Parsley tells you wherever you are in the app rather than leaving it buried in settings.'
      },
      {
        tag: ChangeTag.Improved,
        text: 'Changed your mind about cancelling? **Keep my subscription** calls it off in one tap — and a subscription that has already ended can be started again.'
      },
      { tag: ChangeTag.Improved, text: 'You can log in straight from the landing page — the header and footer used to only offer sign-up.' }
    ]
  },
  {
    shipDate: '2026-08-03',
    version: '2026.0803.0',
    headline: 'Add a dinner to any plan',
    featureTag: ChangeTag.New,
    prose:
      '**You can now add dinners to a plan from your recipe library.** Open "Add a dinner" on a plan you\'re reviewing or one you\'re already cooking, search or filter to find what you want, and pick as many as you like. The picker suggests three to start you off.',
    changes: [
      {
        tag: ChangeTag.New,
        text: '**Food profiles have been redesigned end to end.** A rule is one sheet now instead of three screens, groups let you leave out a whole family and keep the parts that are fine, and every profile has its own page.'
      },
      { tag: ChangeTag.Fixed, text: '**The order you set for your dinners now sticks** — it used to reset when you left the screen.' },
      {
        tag: ChangeTag.Fixed,
        text: '**Removing a dinner sticks too.** It used to come back on reload, and Undo now puts it back on its original day.'
      },
      { tag: ChangeTag.Improved, text: "Meal photos show on your plan's lineup on a phone, not just on a big screen." }
    ]
  },
  {
    shipDate: '2026-07-27',
    version: '2026.0801.1',
    headline: 'Accessibility, top to bottom',
    featureTag: ChangeTag.Improved,
    prose:
      '**Every button, link and field now shows a clear outline when you tab to it.** The plan switcher opens with the keyboard and closes with Escape. Star ratings take arrow keys — and can now be cleared again.',
    changes: [
      { tag: ChangeTag.New, text: 'A real **404 page** — mistyped links land somewhere useful instead of bouncing you home.' },
      { tag: ChangeTag.New, text: '**Arrow buttons** to reorder your draft plan and ingredient swaps, alongside dragging.' },
      { tag: ChangeTag.Improved, text: 'Screen readers announce meals by name while reordering, not internal codes.' },
      { tag: ChangeTag.Fixed, text: 'Long pop-up lists scroll instead of spilling off screen.' },
      { tag: ChangeTag.Fixed, text: 'Green buttons are readable in dark mode, and respond to hover.' },
      { tag: ChangeTag.Fixed, text: 'Dropdown menus no longer flicker when closed quickly.' },
      {
        tag: ChangeTag.Fixed,
        text: "**Tonight's suggested meal stays put** for the whole week — favouriting a recipe no longer swaps it out."
      },
      { tag: ChangeTag.Fixed, text: 'Star ratings can be **cleared** — pick the highlighted star again to go back to unrated.' },
      { tag: ChangeTag.Fixed, text: 'The **Add a dinner** button is visible against cards, and responds when you tap it on your phone.' },
      { tag: ChangeTag.Fixed, text: 'The **Already cooked** section header shows a clear hover state.' },
      { tag: ChangeTag.Fixed, text: 'Delete and discard buttons respond when you hover them, and their count badge is readable.' }
    ]
  },
  {
    shipDate: '2026-07-20',
    version: '2026.0726.1',
    headline: 'The Recipes tab is now your meal library',
    featureTag: ChangeTag.New,
    prose:
      '**Every meal Parsley has ever made you, in one place.** Search by name or ingredient, filter to favorites or never-tried, and send any of them straight back into a plan.',
    changes: [
      { tag: ChangeTag.Improved, text: 'Typography follows **one scale** app-wide — larger body text, smoother headings.' },
      { tag: ChangeTag.Improved, text: 'A broad accessibility pass on focus, navigation and contrast.' },
      { tag: ChangeTag.Fixed, text: 'Parsley **reconnects on its own** after your device has been asleep.' },
      { tag: ChangeTag.Improved, text: 'Food profiles all live in one place now, via **Manage food profiles**.' },
      { tag: ChangeTag.Improved, text: 'Roomier **⋮ menus**, and a tidier mobile bottom bar.' },
      { tag: ChangeTag.Fixed, text: 'The "building plan" popover stays on screen on iPhones.' }
    ]
  },
  {
    shipDate: '2026-07-13',
    version: '2026.0719.0',
    headline: "Undo, for when you didn't mean it",
    featureTag: ChangeTag.New,
    prose:
      '**End a plan early, delete one, discard a draft — all undoable now.** From the toast, with ⌘Z, or from the new undo control in the header, long after the toast has gone.',
    changes: [
      { tag: ChangeTag.New, text: 'A proper first-run guide on Eating Style when you have no profiles yet.' },
      { tag: ChangeTag.Improved, text: 'Ingredient rules are clearer — groups are labelled, common allergens sit up top.' },
      { tag: ChangeTag.Improved, text: 'The Parsley logo and wordmark read the same everywhere.' },
      { tag: ChangeTag.Fixed, text: 'Your profile photo and name update everywhere the moment you change them.' },
      { tag: ChangeTag.Fixed, text: 'The Effort screen saves with only weekdays or only weekends filled in.' },
      { tag: ChangeTag.Fixed, text: '"Browse past plans" now actually opens your cooking history.' }
    ]
  }
]

/**
 * Release notes are content, not UI chrome, so they follow the same locale convention as the
 * privacy policy and terms markdown (see `PrivacyPolicyPage`): a per-locale body with an `en`
 * fallback. Only `en` is written today; add a locale by adding a key here.
 */
const releasesByLocale: Record<string, Release[]> = { en: releases }

export function getReleases(locale: string): Release[] {
  return releasesByLocale[locale] ?? releasesByLocale.en
}
